import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo } from 'react';

import { usePagination, useRouter } from '@boss/hooks';
import { IEvent } from '@boss/services';
import { Alert, Pagination } from '@boss/ui';

import Filters from './filters';
import { EventCardWrapper } from '../../components';
import { useEventFilter } from '../../hooks';
import { eventConfig } from '../../utils';

interface Props {
  events: IEvent[];
}

const GridHeadingStyles = cva('mb-5 sm:col-span-2 lg:col-span-3', {
  variants: {
    variant: {
      primary: '',
      secondary: 'xl:col-span-4',
    },
  },
});

const PaginationStyles = cva('mt-4 flex justify-center md:col-span-2', {
  variants: {
    variant: {
      primary: 'lg:col-span-3',
      secondary: 'lg:col-span-3 xl:col-span-4',
    },
  },
});

const EventsPage = ({ events }: Props) => {
  const { t } = useTranslation(['event', 'stores', 'account']);
  const { locale, query, isReady } = useRouter();

  const initialFilters = useMemo(() => {
    return query.type ||
      query.locationId ||
      query.theme ||
      query.fromDate ||
      query.toDate ||
      query.location ||
      query.locations
      ? {
          type: (query.type as string) ?? null,
          locationId: (query.locationId as string) ?? null,
          theme: (query.theme as string) ?? null,
          fromDate: (query.fromDate as string) ?? null,
          toDate: (query.toDate as string) ?? null,
          location: (query.location as string) ?? null,
          locations: query.locations ? (query.locations as string).split(',') : [],
        }
      : undefined;
  }, [query.type, query.locationId, query.theme, query.fromDate, query.toDate, query.location, query.locations]);

  const {
    filteredEvents,
    updateFilters,
    filters,
    allEventTypes,
    clearFilters,
    allEventLocations,
    availableLanguages,
    allEventThemes,
  } = useEventFilter(events);

  useEffect(() => {
    if (isReady && initialFilters && !filters.type && !filters.location && filters.locations.length === 0) {
      const { type, locationId, theme, fromDate, toDate, location, locations } = initialFilters;
      const _location =
        location || events?.find(event => event.locationId.toString() === locationId?.toString())?.location;

      updateFilters({
        type,
        location: _location,
        locations: locations || [],
        theme,
        fromDate: fromDate ? new Date(fromDate) : null,
        toDate: toDate ? new Date(toDate) : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, initialFilters]);

  const { variant, resultsPerPage } = eventConfig;

  const { currentPage, paginate, currentItems, totalPages } = usePagination(resultsPerPage, filteredEvents);

  const handlePaginate = (pageNumber: number) => {
    paginate(pageNumber);

    const pagination = document.getElementById('pagination');

    if (pagination) {
      // Add small delay to ensure DOM is updated before scrolling
      setTimeout(() => {
        const offset = -800; // Add 800px offset from top
        const paginationPosition = pagination.getBoundingClientRect().top + window.scrollY + offset;

        window.scrollTo({ top: paginationPosition, behavior: 'auto' });
      }, 100);
    }
  };

  return (
    <div className="mt-10">
      <h3 className={GridHeadingStyles({ variant })}>{t('sections.overview.title', { ns: 'event' })}</h3>
      <div className="col-span-4">
        <Filters
          eventLocation={filters.location ?? undefined}
          eventLocations={filters.locations}
          eventType={filters.type ?? undefined}
          eventTypes={allEventTypes}
          fromDate={filters.fromDate?.toISOString()}
          language={filters.language ?? undefined}
          languages={availableLanguages}
          locations={allEventLocations}
          onClearFilters={clearFilters}
          onSetFilters={updateFilters}
          theme={filters.theme ?? undefined}
          themes={allEventThemes.filter((theme): theme is string => theme !== undefined)}
          toDate={filters.toDate?.toISOString()}
        />
      </div>
      <div className="my-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {!events.length && (
          <div className="col-span-4">
            <Alert type="info">{t('sections.overview.noEvents', { ns: 'event' })}</Alert>
          </div>
        )}
        {!!(events.length && !currentItems?.length) && (
          <div className="col-span-4">
            <Alert type="info">{t('sections.overview.noFilteredEvents', { ns: 'event' })}</Alert>
          </div>
        )}

        {currentItems?.map(event => {
          return <EventCardWrapper event={event} key={event.id} locale={locale} />;
        })}
        <div className={PaginationStyles({ variant })} id="pagination">
          <Pagination currentPage={currentPage} onPaginate={handlePaginate} totalPages={totalPages} />
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
